/**
 * openGlobalSearch — cross-host event helper for opening the
 * shared `<GlobalSearch>` palette with a pre-filled query and an
 * optional pre-scoped filter set.
 *
 * @path packages/ui/src/components/global-search/openGlobalSearch.ts
 *
 * WHY THIS EXISTS
 * ---------------
 * The `<GlobalSearch>` palette is mounted by THREE separate hosts
 * in the platform:
 *   1. The master Quotech SPA (`src/app/layouts/unified-layout` →
 *      `<DashboardLayout>` → `useLayoutState.globalSearchOpen`).
 *   2. The Newmark tenant SPA (`apps/newmark/src/shell/
 *      CommandPaletteProvider`).
 *   3. The SAM tenant SPA (`apps/sam/src/shell/
 *      CommandPaletteProvider`).
 *
 * Each host owns its own `isOpen` state behind a private hook —
 * `useLayoutState` and `useCommandPalette` respectively. Deep
 * children (e.g. `<KanbanBoardView>` inside `@quotech/ui/kanban`)
 * have NO React-context handle to those owners. The previous
 * approach of threading "open the palette" callbacks through
 * `KanbanTenantHooks` works for a single host but doesn't scale
 * once the SAME primitive needs to open the palette from inside
 * different hosts.
 *
 * BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED
 * ----------------------------------------------
 * Operator complaint 2026-05-27: "if I am on a board I should be
 * able to search in the board and if I am on the main screen /
 * nothing comes up in the search it should be able to search the
 * rest of the boards.. instead it is a universal search. but I
 * am on Kanban". The in-board `<KanbanBoardView>` search input
 * already filters the loaded card set (`applyFilterState`), but
 * when it returned zero results the operator had no path to
 * widen the search across other boards in the org. The fix is a
 * dedicated fallback affordance inside `<KanbanBoardView>` that
 * dispatches this event with `{query, activeFilters}`; whichever
 * host mounts the palette catches it and pre-seeds the open.
 *
 * CONTRACT
 * --------
 *   - Producers: any module wanting to open the global palette
 *     pre-filled. The kanban view is the first, but the same
 *     event can later power "Search in CRM" / "Search in Email
 *     Hub" affordances without touching the host plumbing.
 *   - Consumers: every `<GlobalSearch>` host installs a
 *     `useEffect` that listens for the event, stores the detail
 *     in local state, and opens the palette. Hosts MUST clear
 *     the stored detail when the palette closes so a manual
 *     re-open (⌘F) starts blank — without the clear, the same
 *     seeded query would leak into the next open.
 *   - Bus: a `CustomEvent` dispatched on `window`. Stable name,
 *     no payload version field — we'll bump the event name if the
 *     contract has to break (e.g. `quotech:open-global-search/v2`).
 *   - Failure mode: dispatching with no listener is a no-op. The
 *     producer should NOT assume the palette opened; this is
 *     fire-and-forget. Consumers that care about telemetry
 *     should derive it from their own open-state transitions.
 */

export const GLOBAL_SEARCH_OPEN_EVENT = 'quotech:open-global-search'

export interface OpenGlobalSearchDetail {
  /** Initial text to seed the palette input with. Trimmed but not
   *  case-normalised; the host's `useSearch` will lowercase via
   *  the standard debounce path. Empty string is allowed — the
   *  palette will simply open without a query. */
  query?: string

  /** Optional list of `filterOptions` ids to pre-activate (e.g.
   *  `['card', 'board', 'lane']` to scope to kanban results, or
   *  `['email']` to scope to inbox hits). When non-empty the host
   *  also reveals the filter chip row so the operator immediately
   *  sees what they've been narrowed to. */
  activeFilters?: string[]

  /** Free-form source string for telemetry / debugging. e.g.
   *  `'kanban-board-fallback'`. Not user-visible. */
  source?: string
}

/**
 * Dispatch the open-global-search event. Safe to call from
 * SSR / non-browser contexts — bails when `window` is undefined.
 *
 * Callers are NOT required to import or use this helper; the
 * raw `CustomEvent` shape is the stable contract. The helper
 * exists for ergonomics + type-safety on the producer side and
 * to give the open-search call a single grep-able call site.
 */
export function openGlobalSearch(detail: OpenGlobalSearchDetail): void {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(
      new CustomEvent<OpenGlobalSearchDetail>(GLOBAL_SEARCH_OPEN_EVENT, {
        detail,
      }),
    )
  } catch {
    // CustomEvent constructor isn't available in very old envs.
    // Best-effort only; the operator can still hit ⌘F manually.
  }
}

/**
 * Type guard / extractor for the event detail. Hosts use this in
 * their listener to avoid casting in three places.
 */
export function getOpenGlobalSearchDetail(
  ev: Event,
): OpenGlobalSearchDetail | null {
  if (!('detail' in ev)) return null
  const detail = (ev as CustomEvent).detail
  if (!detail || typeof detail !== 'object') return null
  const d = detail as Partial<OpenGlobalSearchDetail>
  return {
    query: typeof d.query === 'string' ? d.query : undefined,
    activeFilters: Array.isArray(d.activeFilters)
      ? d.activeFilters.filter((f): f is string => typeof f === 'string')
      : undefined,
    source: typeof d.source === 'string' ? d.source : undefined,
  }
}
